import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Briefcase, MessageSquare, Target, Zap, Save, Trash2, Plus, CheckCircle2 } from "lucide-react";
import { getBackend } from "../lib/backend";
import { ConfirmModal } from "./ConfirmModal";

interface Brand {
    id?: number;
    name: string;
    description: string;
    product: string;
    tone: string;
    goal: string;
    is_active: number;
}

interface BrandProfileProps {
    onActiveBrandChange: () => Promise<void>;
}

const BrandProfile: React.FC<BrandProfileProps> = ({ onActiveBrandChange }) => {

    const [brands, setBrands] = useState<Brand[]>([]);
    const [activeBrand, setActiveBrand] = useState<Brand | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<Brand>({
        name: "",
        description: "",
        product: "",
        tone: "",
        goal: "",
        is_active: 0
    });

    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

    const fetchBrands = async () => {
        const backend = await getBackend();
        const data = await backend.getBrands();
        setBrands(data);

        let active = data.find((b: Brand) => b.is_active === 1);

        // Auto-select if there is only one brand and it's not active
        if (!active && data.length === 1) {
            const singleBrand = data[0];
            await backend.updateBrand(singleBrand.id!, { ...singleBrand, is_active: 1 });
            active = { ...singleBrand, is_active: 1 };
            // Refresh list to reflect backend change
            fetchBrands();
            onActiveBrandChange();
            return;
        }

        if (active) setActiveBrand(active);
    };

    useEffect(() => {
        fetchBrands();
    }, []);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        const backend = await getBackend();

        if (formData.id) {
            await backend.updateBrand(formData.id, { ...formData, is_active: activeBrand ? 0 : 1 });
        } else {
            await backend.createBrand({ ...formData, is_active: activeBrand ? 0 : 1 });
        }

        setIsEditing(false);
        setFormData({ name: "", description: "", product: "", tone: "", goal: "", is_active: 0 });
        fetchBrands();
    };

    const toggleActive = async (brand: Brand) => {
        const backend = await getBackend();
        // Deactivate all others, activate this one
        for (const b of brands) {
            if (b.id === brand.id) {
                await backend.updateBrand(b.id!, { ...b, is_active: 1 });
            } else if (b.is_active === 1) {
                await backend.updateBrand(b.id!, { ...b, is_active: 0 });
            }
        }
        fetchBrands();
        onActiveBrandChange();
    };

    const handleDelete = async (id: number) => {
        const backend = await getBackend();
        await backend.deleteBrand(id);
        fetchBrands();
        setConfirmDeleteId(null);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row items-start gap-2 justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-white">Brand Profiles</h2>
                    <p className="text-white/60 text-sm">Manage company voice and personas</p>
                </div>
                <button
                    onClick={() => {
                        setIsEditing(true);
                        setFormData({ name: "", description: "", product: "", tone: "", goal: "", is_active: 0 });
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 rounded-xl transition-all"
                >
                    <Plus size={18} />
                    <span>New Profile</span>
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AnimatePresence>
                    {brands.map((brand) => (
                        <motion.div
                            key={brand.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className={`relative p-6 rounded-2xl border transition-all ${brand.is_active
                                ? "bg-primary/10 border-primary/50 ring-1 ring-primary/20"
                                : "bg-white/5 border-white/10 hover:border-white/20"
                                }`}
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg ${brand.is_active ? "bg-primary/20 text-primary" : "bg-white/10 text-white/60"}`}>
                                        <Briefcase size={20} />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white">{brand.name}</h3>
                                        {brand.is_active === 1 && (
                                            <span className="text-[10px] uppercase tracking-wider text-primary font-bold">Active Voice</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => {
                                            setFormData(brand);
                                            setIsEditing(true);
                                        }}
                                        className="p-2 hover:bg-white/10 text-white/40 hover:text-white rounded-lg transition-colors"
                                    >
                                        <Save size={16} />
                                    </button>
                                    <button
                                        onClick={() => setConfirmDeleteId(brand.id!)}
                                        className="p-2 hover:bg-red-500/10 text-white/40 hover:text-red-400 rounded-lg transition-colors"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-3 mb-6">
                                <p className="text-sm text-white/60 line-clamp-2">{brand.description}</p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="px-2 py-1 bg-white/5 text-white/40 text-[10px] rounded-md border border-white/10">Tone: {brand.tone}</span>
                                    <span className="px-2 py-1 bg-white/5 text-white/40 text-[10px] rounded-md border border-white/10">Goal: {brand.goal}</span>
                                </div>
                            </div>

                            <button
                                onClick={() => toggleActive(brand)}
                                disabled={brand.is_active === 1}
                                className={`w-full py-2.5 rounded-xl text-sm font-medium transition-all ${brand.is_active
                                    ? "bg-primary/20 text-primary cursor-default"
                                    : "bg-white/5 hover:bg-white/10 text-white border border-white/10"
                                    }`}
                            >
                                {brand.is_active ? "Currently Selected" : "Set as Active"}
                            </button>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>

            {/* Editor Modal Overlay */}
            <AnimatePresence>
                {isEditing && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsEditing(false)}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-xl z-[70] p-4"
                        >
                            <div className="bg-[#0f1115] border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
                                <div className="p-6 border-b border-white/5 flex items-center justify-between">
                                    <h3 className="text-xl font-bold text-white">{formData.id ? "Edit Profile" : "New Brand Profile"}</h3>
                                    <button onClick={() => setIsEditing(false)} className="text-white/40 hover:text-white">✕</button>
                                </div>

                                <form onSubmit={handleSave} className="p-6 space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-white/40 uppercase tracking-wider px-1">Company Name</label>
                                        <input
                                            required
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="e.g. Tekibo AI"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/20 focus:outline-none focus:border-primary/50 transition-colors"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-xs font-semibold text-white/40 uppercase tracking-wider px-1 text-nowrap">Main Product</label>
                                            <input
                                                required
                                                value={formData.product}
                                                onChange={e => setFormData({ ...formData, product: e.target.value })}
                                                placeholder="e.g. AI Content Tools"
                                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/20 focus:outline-none focus:border-primary/50 transition-colors"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-semibold text-white/40 uppercase tracking-wider px-1">Voice Tone</label>
                                            <input
                                                required
                                                value={formData.tone}
                                                onChange={e => setFormData({ ...formData, tone: e.target.value })}
                                                placeholder="e.g. Professional, Friendly"
                                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/20 focus:outline-none focus:border-primary/50 transition-colors"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-white/40 uppercase tracking-wider px-1">Description</label>
                                        <textarea
                                            required
                                            rows={3}
                                            value={formData.description}
                                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                                            placeholder="What does your company do?"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/20 focus:outline-none focus:border-primary/50 transition-colors resize-none"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-white/40 uppercase tracking-wider px-1">Primary Goal</label>
                                        <input
                                            required
                                            value={formData.goal}
                                            onChange={e => setFormData({ ...formData, goal: e.target.value })}
                                            placeholder="e.g. Drive sales, Build engagement"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/20 focus:outline-none focus:border-primary/50 transition-colors"
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        className="w-full mt-4 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-primary/90 transition-all flex items-center justify-center gap-2"
                                    >
                                        <Save size={20} />
                                        <span>Save Brand Profile</span>
                                    </button>
                                </form>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            <ConfirmModal
                isOpen={confirmDeleteId !== null}
                onClose={() => setConfirmDeleteId(null)}
                onConfirm={() => confirmDeleteId && handleDelete(confirmDeleteId)}
                title="Delete Brand Profile"
                message="Are you sure you want to delete this brand profile? This action will permanently remove it."
                confirmText="Delete"
                variant="destructive"
            />
        </div>
    );
};

export default BrandProfile;
